<!--
This component displays a Hydrogen Product Management image.

Basic Usage:

<product-image :image="image" :width="100" height="100">

Where `image` is `json_encode(new ProductImage(...))`
-->

<template>
	<img :src="url" :width="width" :height="height" :alt="alt" :title="alt">
</template>

<script>

/**
 * Encode a variable for the image URL.
 *
 * @param {mixed} value The value to encode.  This may be a string, number, or object.
 * @param {String} key Used internally to recurse when the value passed is an object.
 *
 * @return {String} Returns the value encoded as a string.
 */
function encode(value, key)
{
	if (typeof value === 'object')
	{
		const keys = Object.keys(value);
		let str = '';

		for (let i = 0; i < keys.length; ++i)
		{
			const k = keys[i];
			const v = value[k];

			if (str)
				str += '-';

			str += encode(key) + '[' + encode(k) + ']-' + encode(v);
		}

		return str;
	}

	if (/[^a-z0-9 .]|^_/i.test(value))
		return '_' + btoa(value);

	return value;
}

/**
 * Get the full URL for a product image.
 *
 * @param {Integer} productType The product type ID, as defined in product management.
 * @param {Integer} productId The product ID, as defined in product management.
 * @param {String} name The name of the image file, including extension.
 * @param {String} type The name of the image class to use, for example 'Basic' or 'BasicCrop'.
 * @param {Object} options An associative array of options to pass to the image class.  The options available depend on the class chosen in the type parameter.
 * @param {String} imageType The type of image desired.  May be one of 'png', 'gif', 'jpg'.
 *
 * @return {String} Returns the image URL.
 */
function getUrl(productType, productId, name, type, options, imageType)
{
	const optionKeys = Object.keys(options);
	optionKeys.sort();
	let encodedOptions = [];

	for (let i = 0; i < optionKeys.length; ++i)
	{
		const optionKey = optionKeys[i];
		const optionValue = options[optionKey];

		if (typeof optionValue === 'object')
			encodedOptions.push(encode(optionValue, optionKey));
		else
			encodedOptions.push(encode(optionKey) + '-' + encode(optionValue));
	}

	encodedOptions = encodedOptions.join('-');

	if (encodedOptions)
		encodedOptions = '-' + encodedOptions;

	return ASSETS + '/images/' + encodeURIComponent(productType + '-' + productId + '-' + encode(name) + '-' + encode(type) + encodedOptions + '.' + imageType);
}

export default {
	props: {
		// The image, a Hydrogen ProductImage.
		image: {
			type: Object,
			required: true,
		},

		// The width of the image in pixels.  Defaults to the natural width.
		width: {
			type: Number,
			default: null,
		},

		// The height of the image in pixels.  Defaults to the natural height.
		height: {
			type: Number,
			default: null,
		},

		// The image file format, one of: jpg, png, gif.
		type: {
			type: String,
			default: 'jpg',
		},

		// The ImageFactory class, e.g. 'Basic', 'BasicCrop'.
		imageClass: {
			type: String,
			default: 'BasicCrop',
		},
	},
	computed: {
		/**
		 * Compute the URL of the image.
		 *
		 * @return {String} Returns the URL of the image.
		 */
		url: function () {
			let options = {};

			// If the width and height are specified, set the size option.
			if (this.width && this.height)
				options.size = this.width + 'x' + this.height;

			return getUrl(this.image.productComponentId, this.image.productId, this.image.name, this.imageClass, options, this.type);
		},

		/**
		 * Get the alternative text for the image.
		 *
		 * @return {String} Returns the text for the image's `alt` attribute.
		 */
		alt: function () {
			return this.image.alt;
		},
	}
}
</script>
